import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Shield, AlertTriangle, Eye } from 'lucide-react';

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
      });

      if (response.data.success) {
        onLogin(response.data.token);
      }
    } catch (err) {
      setError('Invalid credentials. Access denied.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1712508818413-76a31994b525?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      </div>

      {/* Scanline Effect */}
      <div className="absolute inset-0 scanline opacity-20 pointer-events-none z-10" />

      {/* Login Box */}
      <div className="relative z-20 w-full max-w-md mx-4">
        <div className="glass-panel tactical-border p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-[#00FF9D]" strokeWidth={1.5} />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              DEFENSEWATCH
            </h1>
            <p className="text-xs text-zinc-400 uppercase tracking-widest font-mono">
              Military Intelligence Platform
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-[#FFD60A] text-xs">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-mono">CLASSIFIED ACCESS REQUIRED</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">
                User Identification
              </label>
              <input
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-black/50 border border-white/10 focus:border-[#00FF9D]/50 text-white px-4 py-3 text-sm font-mono outline-none transition-colors"
                placeholder="Enter username"
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">
                Security Credential
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-black/50 border border-white/10 focus:border-[#00FF9D]/50 text-white px-4 py-3 text-sm font-mono outline-none transition-colors"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error-message" className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-mono">{error}</span>
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="font-mono">AUTHENTICATING...</span>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span>ACCESS SYSTEM</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
              UNCLASSIFIED // FOR OFFICIAL USE ONLY
            </p>
            <p className="text-[10px] text-zinc-600 mt-2 font-mono">
              © 2026 DefenseWatch Platform v1.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}